import { OfflineViewState, SceneConfig as OctreeSceneConfig, View } from "@novorender/api";
import { ObjectId } from "@novorender/data-js-api";
import type { Webviewer } from "lib";
import { createContext, Dispatch } from "react";

import { AppObjectDB } from "utils/appObjectDb";
import { EventEmitter } from "utils/EventEmitter";

export const initialState = {
    view: undefined as undefined | View,
    scene: undefined as undefined | OctreeSceneConfig,
    db: undefined as undefined | AppObjectDB,
    canvas: null as null | HTMLCanvasElement,
    size: { width: 0, height: 0 },
    offlineWorkerState: undefined as undefined | OfflineViewState,
    availableObjectIds: undefined as undefined | Set<ObjectId>,
    forbiddenObjectIds: undefined as undefined | Uint32Array,
    webviewer: undefined as undefined | Webviewer,
    // Internal EventEmitter for app mode - always available
    internalEventEmitter: new EventEmitter(),
};

enum ActionTypes {
    Set,
    Update,
}

function update(updates: Partial<State>) {
    return {
        type: ActionTypes.Update as const,
        updates,
    };
}

function set(state: State) {
    return {
        type: ActionTypes.Set as const,
        state,
    };
}

export const actions = { set, update };

type State = typeof initialState;
export type HydratedState = Pick<
    { [K in keyof State]: NonNullable<State[K]> },
    "view" | "scene" | "db" | "canvas" | "offlineWorkerState" | "internalEventEmitter"
> &
    Omit<State, "view" | "scene" | "db" | "canvas" | "offlineWorkerState" | "internalEventEmitter">;

export type Actions = ReturnType<(typeof actions)[keyof typeof actions]>;
export type ContextType = { state: State | HydratedState; dispatch: Dispatch<Actions> };

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const Context = createContext<ContextType>(undefined as any);

export function reducer(state: State, action: Actions): State {
    switch (action.type) {
        case ActionTypes.Set: {
            return action.state;
        }
        case ActionTypes.Update: {
            return {
                ...state,
                ...action.updates,
            };
        }
        default: {
            throw new Error(`Unhandled action type`);
        }
    }
}
